import { useCallback, useEffect, useMemo, useState } from "react";
import { usePost } from "./API";
import { categorizeSignedURLs, snakeToEnglish, fileNameFromUrl } from "./Util";
import { DataTable, Modal, Badge, Spinner, useToast, Select, Alert, Button, TextArea, Tooltip, Icon, SelectOptionType } from "@aidkitorg/component-library";
import { TableAction } from "@aidkitorg/component-library/dist/types/src/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import { FieldConfig } from "@aidkitorg/aidkit/lib/documents/filehub";

interface FileHubDocument {
  url: string;
  created_at: string;
  source: string;
  target_field: string | null;
  latest: boolean | null;
}

function renderMediaPreview(params: {
  imageUrl: string | null,
  videoUrl: string | null,
  audioUrl: string | null,
  alt?: string,
}) {
  const { imageUrl, videoUrl, audioUrl, alt = '' } = params;
  const hasMedia = imageUrl || videoUrl || audioUrl;

  return (
    <div className="overflow-auto">
      {videoUrl && (
        <video className="w-full rounded" controls crossOrigin="anonymous" src={videoUrl} />
      )}
      {audioUrl && (
        <audio className="w-full" controls preload="auto">
          <source src={audioUrl} type="audio/mpeg" />
        </audio>
      )}
      {imageUrl && (
        <img alt={alt} className="max-w-full rounded" src={imageUrl} />
      )}
      {!hasMedia && (
        <p className="text-gray-500 py-8 text-center">Unable to load file preview.</p>
      )}
    </div>
  );
}

export function FileHub(props: { applicant?: string }) {
  const getDocuments = usePost("/applicant/get_documents");
  const getDocumentTargetFields = usePost("/applicant/get_document_target_fields");
  const getSignedURL = usePost("/v2/document/view");

  // Document target fields are specified in program config as target fields that contain documents.
  const [documentTargetFields, setDocumentTargetFields] = useState<Record<string, FieldConfig>>({});
  const [documents, setDocuments] = useState<FileHubDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const { toast } = useToast();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [copyToModalOpen, setCopyToModalOpen] = useState(false);
  const [copyToFileUrl, setCopyToFileUrl] = useState<string | null>(null);
  const [copyToImageUrl, setCopyToImageUrl] = useState<string | null>(null);
  const [copyToVideoUrl, setCopyToVideoUrl] = useState<string | null>(null);
  const [copyToAudioUrl, setCopyToAudioUrl] = useState<string | null>(null);
  const [copyToLoading, setCopyToLoading] = useState(false);
  const [copyToExplanation, setCopyToExplanation] = useState("");
  const [selectedTargetField, setSelectedTargetField] = useState<SelectOptionType | undefined>(undefined);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeFileUrl, setRemoveFileUrl] = useState<string | null>(null);
  const [removeTargetField, setRemoveTargetField] = useState<string | null>(null);
  const [removeImageUrl, setRemoveImageUrl] = useState<string | null>(null);
  const [removeVideoUrl, setRemoveVideoUrl] = useState<string | null>(null);
  const [removeAudioUrl, setRemoveAudioUrl] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeExplanation, setRemoveExplanation] = useState("");

  useEffect(() => {
    if (!props.applicant) return;
    (async () => {
      try {
        setLoading(true);
        const result = await getDocuments({ applicant: props.applicant! });
        setDocuments(result?.documents ?? []);

        const targetFieldsResult = await getDocumentTargetFields({});
        setDocumentTargetFields(targetFieldsResult?.documentTargetFields ?? {});

      } catch (e) {
        console.error("Failed to fetch documents:", e);
        toast({ description: `Failed to fetch documents: ${e}`, variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [props.applicant]);

  const openFile = useCallback(async (url: string) => {
    setModalTitle(fileNameFromUrl(url));
    setModalOpen(true);
    setModalLoading(true);
    setImageUrl(null);
    setVideoUrl(null);
    setAudioUrl(null);

    try {
      const result = await getSignedURL({ paths: [url] });
      if (result?.paths?.[url]) {
        const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[url]);
        setImageUrl(imageFileUrls[0]);
        setVideoUrl(videoFileUrls[0]);
        setAudioUrl(audioFileUrls[0]);
      }
    } catch (error) {
      console.error("Failed to fetch signed URL:", error);
    } finally {
      setModalLoading(false);
    }
  }, []);

  const columns = useMemo<ColumnDef<FileHubDocument, any>[]>(() => [
    {
      id: "file",
      header: "File",
      accessorFn: (row) => fileNameFromUrl(row.url),
      cell: ({ row }) => (
        <button
          onClick={() => openFile(row.original.url)}
          className="text-indigo-600 hover:text-indigo-800 underline break-all text-left cursor-pointer bg-transparent border-none p-0"
        >
          {fileNameFromUrl(row.original.url)}
        </button>
      ),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (row) => row.source,
      cell: ({ row }) => <span>{row.original.source?.toLowerCase() === 'system' ? 'Applicant' : row.original.source}</span>,
    },
    {
      id: "target_field",
      header: "Location",
      accessorFn: (row) => row.target_field ? snakeToEnglish(row.target_field) : "--",
      cell: ({ row }) => row.original.target_field ? <div className="flex items-center gap-2">
        <Tooltip 
          variant="light"
          text={<div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              {documentTargetFields[row.original.target_field]?.displayName ? <span>{documentTargetFields[row.original.target_field]?.displayName}:</span> : <code className="text-black">{row.original.target_field}:</code>}
              {documentTargetFields[row.original.target_field]?.description ? <div className="text-gray-500 text-sm">{documentTargetFields[row.original.target_field]?.description}</div> : <div className="text-gray-500 text-sm">the field that this document lives on.</div>}
            </div>
            <div>
              {row.original.latest 
                ? <span>This document is currently live on this attachment field.</span> 
                : <span>This document was last seen on this attachment, but does not live on this attachment anymore.</span>}
            </div>
          </div>}
        >
          <Icon name="InformationCircleIcon" className="w-4 h-4" />
        </Tooltip>
        {documentTargetFields[row.original.target_field]?.displayName ? <span>{documentTargetFields[row.original.target_field]?.displayName}</span> : <code className="text-black">{row.original.target_field}</code>}
        {row.original.latest ? <Badge variant="success">Current</Badge> : <></>}
      </div> : "--",
    },
    {
      id: "created_at",
      header: "Last modified",
      accessorFn: (row) => row.created_at,
      cell: ({ row }) => {
        const date = new Date(row.original.created_at);
        return isNaN(date.getTime()) ? row.original.created_at : date.toLocaleString();
      },
    }
  ], [openFile, documentTargetFields]);

  const actions = useMemo<TableAction<FileHubDocument>[]>(() => [
    {
      label: "View File",
      onClick: (row: FileHubDocument) => openFile(row.url),
      icon: 'EyeIcon',
    },
    {
      label: "Copy To",
      onClick: async (row: FileHubDocument) => {
        setSelectedTargetField(undefined);
        setCopyToExplanation("");
        setCopyToModalOpen(true);
        setCopyToFileUrl(row.url);
        setCopyToImageUrl(null);
        setCopyToVideoUrl(null);
        setCopyToAudioUrl(null);
        setCopyToLoading(true);
        try {
          const result = await getSignedURL({ paths: [row.url] });
          if (result?.paths?.[row.url]) {
            const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[row.url]);
            setCopyToImageUrl(imageFileUrls[0] ?? null);
            setCopyToVideoUrl(videoFileUrls[0] ?? null);
            setCopyToAudioUrl(audioFileUrls[0] ?? null);
          }
        } catch (error) {
          console.error("Failed to fetch signed URL for copy-to preview:", error);
        } finally {
          setCopyToLoading(false);
        }
      },
      icon: 'DocumentDuplicateIcon',
    },
    {
      label: "History",
      onClick: () => setHistoryModalOpen(true),
      icon: 'ClockIcon',
    },
    {
      label: "Remove From Attachment",
      onClick: async (row: FileHubDocument) => {
        setRemoveExplanation("");
        setRemoveModalOpen(true);
        setRemoveFileUrl(row.url);
        setRemoveTargetField(row.target_field);
        setRemoveImageUrl(null);
        setRemoveVideoUrl(null);
        setRemoveAudioUrl(null);
        setRemoveLoading(true);
        try {
          const result = await getSignedURL({ paths: [row.url] });
          if (result?.paths?.[row.url]) {
            const { imageFileUrls, videoFileUrls, audioFileUrls } = categorizeSignedURLs(result.paths[row.url]);
            setRemoveImageUrl(imageFileUrls[0] ?? null);
            setRemoveVideoUrl(videoFileUrls[0] ?? null);
            setRemoveAudioUrl(audioFileUrls[0] ?? null);
          }
        } catch (error) {
          console.error("Failed to fetch signed URL for remove preview:", error);
        } finally {
          setRemoveLoading(false);
        }
      },
      icon: 'XMarkIcon',
      hideWhen: (row: FileHubDocument) => !row.latest,
      disableWhen: (row: FileHubDocument) => {
        if (!row.target_field) return true;
        return documents.filter(d => d.target_field === row.target_field && d.latest).length <= 1;
      },
    },
  ], [openFile, documents]);

  const targetFieldOptions = useMemo<SelectOptionType[]>(() =>
    Object.entries(documentTargetFields).map(([key, config]) => ({
      label: config.displayName || snakeToEnglish(key),
      value: key,
    })), [documentTargetFields]);

  const selectedFieldDescription = selectedTargetField
    ? documentTargetFields[selectedTargetField.value]?.description
    : undefined;

  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">File Hub</h1>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">File Hub</h1>
      {documents.length === 0 ? (
        <p className="text-gray-500">No documents found for this applicant.</p>
      ) : (
        <DataTable
          columns={columns}
          data={documents}
          actions={actions}
          getRowId={(row) => `${row.target_field}-${row.url}`}
          showPagination={true}
          enableSearch={true}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={setModalOpen}
        title={modalTitle}
        size="lg"
      >
        {modalLoading ? (
          <p className="text-gray-500 py-8 text-center">Loading file...</p>
        ) : (
          renderMediaPreview({ imageUrl, videoUrl, audioUrl, alt: modalTitle })
        )}
      </Modal>

      <Modal
        open={historyModalOpen}
        onClose={setHistoryModalOpen}
        title="File History"
        size="lg"
      >
        <p className="text-gray-500 py-8 text-center">File History</p>
      </Modal>

      <Modal
        open={copyToModalOpen}
        onClose={setCopyToModalOpen}
        title="Copy Document To"
        size="md"
      >
        <div className="flex flex-col gap-4">
          <div className="mt-2">Copying this document to a new location will not remove it from its existing location.</div>
          {copyToLoading ? (
            <p className="text-gray-500 py-8 text-center">Loading preview...</p>
          ) : (
            renderMediaPreview({ imageUrl: copyToImageUrl, videoUrl: copyToVideoUrl, audioUrl: copyToAudioUrl, alt: copyToFileUrl ?? '' })
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Copy To:</span>
            <div className="flex-1">
              <Select
                label="Location"
                hideLabel
                description=""
                options={targetFieldOptions}
                value={selectedTargetField}
                onChange={(value) => setSelectedTargetField(value)}
                clearable
              />
            </div>
          </div>
          {selectedFieldDescription && selectedTargetField && (
            <Alert variant="primary" className="p-2">
              <div>Field: {selectedTargetField.value}</div>
              <div>Description: {selectedFieldDescription}</div>
            </Alert>
          )}
          <TextArea
            label="Reason for copy"
            placeholder="Briefly explain why this document is being copied..."
            value={copyToExplanation}
            onChange={(e) => setCopyToExplanation(e.target.value)}
            rows={3}
          />
          <Button
            variant="primary"
            onClick={() => {}}
            isDisabled={!copyToExplanation.trim() || !selectedTargetField}
          >
            Copy
          </Button>
        </div>
      </Modal>

      <Modal
        open={removeModalOpen}
        onClose={setRemoveModalOpen}
        title="Remove From Attachment"
        size="md"
      >
        <div className="flex flex-col gap-4">
          {removeLoading ? (
            <p className="text-gray-500 py-8 text-center">Loading preview...</p>
          ) : (
            renderMediaPreview({ imageUrl: removeImageUrl, videoUrl: removeVideoUrl, audioUrl: removeAudioUrl, alt: removeFileUrl ?? '' })
          )}
          <p className="text-sm text-gray-700">
            This will remove the document from <strong>{removeTargetField ? snakeToEnglish(removeTargetField) : 'this field'}</strong>.
            The document will still be available in the File Hub and can be added back at any time.
          </p>
          <TextArea
            label="Reason for removal"
            placeholder="Briefly explain why this document is being removed..."
            value={removeExplanation}
            onChange={(e) => setRemoveExplanation(e.target.value)}
            rows={3}
          />
          <Button
            variant="primary"
            onClick={() => {}}
            isDisabled={!removeExplanation.trim()}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
}
